import { useState, useEffect, useCallback } from 'react'
import { Tags, Save, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown, RotateCcw } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import { inputStyle, cardStyle, labelStyle, hintStyle, smallBtn, iconBtn, textareaStyle, subCardStyle, rowHeadStyle, rowLabelStyle, Field, Area, Toggle } from './ui'

// Packages — the Sponsorship Packages pricing grid on the PUBLIC Sponsors page
// (watch.tenband.tv/advertise), rendered directly above the Shopify offer cards.
// Stored in app_settings under the single `placements_pricing` key, delivered via
// vader /api/app-config → watch /api/config `sponsorships.pricing` → PricingChart.
//
// Split out of the Placements editor because the two edit DIFFERENT public pages —
// this one drives /advertise, Placements drives /ad-placements — and burying the price
// fields four cards below the ad-placement tables made them hard to find.
//
// Saving writes ONLY `placements_pricing`. The settings endpoint upserts per key, so the
// two editors can save independently without clobbering each other.

const TONE_CHOICES = [
  { value: 'red', label: 'Red' },
  { value: 'green', label: 'Green' },
  { value: 'amber', label: 'Amber' },
]

export default function Packages() {
  const { api } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')
  const [pricing, setPricing] = useState({ enabled: true, heading: '', intro: '', note: '', features: [], tiers: [] })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const cfg = await api('/app-config')
      const pr = cfg?.sponsorships?.pricing || {}
      setPricing({
        enabled: pr.enabled !== false,
        heading: pr.heading || '',
        intro: pr.intro || '',
        note: pr.note || '',
        features: Array.isArray(pr.features) ? pr.features : [],
        tiers: (Array.isArray(pr.tiers) ? pr.tiers : []).map(t => ({
          id: t.id || '',
          name: t.name || '',
          price: t.price || '',
          price_note: t.price_note || '',
          blurb: t.blurb || '',
          badge: t.badge || '',
          badge_tone: t.badge_tone || 'red',
          featured: !!t.featured,
          enabled: t.enabled !== false,
          includes: Array.isArray(t.includes) ? t.includes.map(Boolean) : [],
        })),
      })
    } catch (e) {
      setStatus(`Error loading: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  /* Every tier's `includes` array is positional against `features`, so adding, removing, or
     reordering a line item must apply the SAME operation to every tier in one update —
     otherwise the grid misaligns and a package advertises the wrong thing. */

  const updateTier = (ti, patch) =>
    setPricing(prev => ({ ...prev, tiers: prev.tiers.map((t, i) => (i === ti ? { ...t, ...patch } : t)) }))

  const toggleInclude = (ti, fi) =>
    setPricing(prev => ({
      ...prev,
      tiers: prev.tiers.map((t, i) =>
        i === ti ? { ...t, includes: t.includes.map((v, j) => (j === fi ? !v : v)) } : t
      ),
    }))

  const setFeature = (fi, value) =>
    setPricing(prev => ({ ...prev, features: prev.features.map((f, i) => (i === fi ? value : f)) }))

  const addFeature = () =>
    setPricing(prev => ({
      ...prev,
      features: [...prev.features, 'New line item'],
      tiers: prev.tiers.map(t => ({ ...t, includes: [...t.includes, false] })),
    }))

  const removeFeature = fi =>
    setPricing(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== fi),
      tiers: prev.tiers.map(t => ({ ...t, includes: t.includes.filter((_, i) => i !== fi) })),
    }))

  const moveFeature = (fi, dir) => {
    const to = fi + dir
    if (to < 0 || to >= pricing.features.length) return
    setPricing(prev => {
      const swap = arr => { const n = [...arr]; const [m] = n.splice(fi, 1); n.splice(to, 0, m); return n }
      return { ...prev, features: swap(prev.features), tiers: prev.tiers.map(t => ({ ...t, includes: swap(t.includes) })) }
    })
  }

  const addTier = () =>
    setPricing(prev => ({
      ...prev,
      tiers: [...prev.tiers, {
        id: `tier-${prev.tiers.length + 1}`, name: 'New package', price: '', price_note: '',
        blurb: '', badge: '', badge_tone: 'red', featured: false, enabled: true,
        includes: prev.features.map(() => false),
      }],
    }))

  const removeTier = ti => {
    if (!confirm('Remove this package from the pricing chart?')) return
    setPricing(prev => ({ ...prev, tiers: prev.tiers.filter((_, i) => i !== ti) }))
  }

  const moveTier = (ti, dir) => {
    const to = ti + dir
    if (to < 0 || to >= pricing.tiers.length) return
    setPricing(prev => {
      const tiers = [...prev.tiers]
      const [m] = tiers.splice(ti, 1)
      tiers.splice(to, 0, m)
      return { ...prev, tiers }
    })
  }

  async function save() {
    // Mirrors the worker's validator so a bad chart fails here with a clear message.
    if (!pricing.features.length) { setStatus('Error: the chart needs at least one line item.'); return }
    const ids = new Set()
    for (const [i, t] of pricing.tiers.entries()) {
      const id = t.id.trim()
      if (!id) { setStatus(`Error: package ${i + 1} needs an id.`); return }
      if (ids.has(id)) { setStatus(`Error: duplicate package id "${id}".`); return }
      ids.add(id)
      if (!t.name.trim()) { setStatus(`Error: package "${id}" needs a name.`); return }
      if (t.includes.length !== pricing.features.length) {
        setStatus(`Error: package "${id}" is out of sync with the line items — reload and try again.`); return
      }
    }

    setSaving(true); setStatus('')
    try {
      await api('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          settings: {
            placements_pricing: JSON.stringify({
              enabled: pricing.enabled,
              heading: pricing.heading.trim(),
              intro: pricing.intro.trim(),
              note: pricing.note.trim(),
              features: pricing.features.map(f => f.trim()),
              tiers: pricing.tiers.map(t => ({
                id: t.id.trim(),
                name: t.name.trim(),
                price: t.price.trim(),
                price_note: t.price_note.trim(),
                blurb: t.blurb.trim(),
                badge: t.badge.trim(),
                badge_tone: t.badge_tone,
                featured: !!t.featured,
                enabled: t.enabled,
                includes: t.includes.map(Boolean),
              })),
            }),
          },
        }),
      })
      setStatus('Saved. The Sponsors page reflects changes within ~5 min (/api/config cache).')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  // Clearing the key makes /api/app-config fall back to the worker's built-in defaults.
  async function restoreDefaults() {
    if (!confirm('Discard your pricing chart and restore the built-in defaults?')) return
    setSaving(true); setStatus('')
    try {
      await api('/settings', { method: 'PUT', body: JSON.stringify({ settings: { placements_pricing: '' } }) })
      await load()
      setStatus('Restored the built-in defaults.')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ color: 'var(--color-text-muted)' }}>Loading…</div>

  return (
    <div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: '0 0 20px', lineHeight: 1.5, display: 'flex', gap: 8 }}>
        <Tags size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          The <strong>Sponsorship Packages</strong> pricing grid on <code>watch.tenband.tv/advertise</code>, shown
          directly above the Shopify offers. Line items run down the side, packages across the top.
        </span>
      </p>

      {/* Section copy */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Section</h3>
        <Toggle on={pricing.enabled} onClick={() => setPricing(p => ({ ...p, enabled: !p.enabled }))}>
          Show the pricing chart on the Sponsors page.
        </Toggle>
        <Field label="Heading" value={pricing.heading} onChange={v => setPricing(p => ({ ...p, heading: v }))} />
        <Area label="Intro" value={pricing.intro} onChange={v => setPricing(p => ({ ...p, intro: v }))} rows={2} />
        <Area
          label="Note under the chart"
          value={pricing.note}
          onChange={v => setPricing(p => ({ ...p, note: v }))}
          rows={6}
          hint="Shown in bold white under the chart. Separate paragraphs with a blank line. The words “Contact us” become a link to the sponsorship contact address (set on the Ad Placements tab)."
        />
      </div>

      {/* Line items */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px' }}>Line items</h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 12px' }}>
          The rows down the left of the chart. Adding, removing, or reordering one updates every package below
          automatically so the columns stay aligned.
        </p>
        {pricing.features.map((f, fi) => (
          <div key={fi} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input type="text" value={f} onChange={e => setFeature(fi, e.target.value)} style={{ ...inputStyle, fontSize: 12 }} />
            <button type="button" title="Move up" onClick={() => moveFeature(fi, -1)} style={iconBtn}><ChevronUp size={12} /></button>
            <button type="button" title="Move down" onClick={() => moveFeature(fi, 1)} style={iconBtn}><ChevronDown size={12} /></button>
            <button type="button" title="Remove" onClick={() => removeFeature(fi)} style={iconBtn}><Trash2 size={12} /></button>
          </div>
        ))}
        <button type="button" onClick={addFeature} style={smallBtn}>
          <Plus size={12} /> Add line item
        </button>
      </div>

      {/* Packages */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 4px' }}>Packages</h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 12px' }}>
          One column per package, left to right. Toggle which line items each one includes.
        </p>

        {pricing.tiers.map((t, ti) => (
          <div key={ti} style={{ ...subCardStyle, opacity: t.enabled ? 1 : 0.6 }}>
            <div style={rowHeadStyle}>
              <span style={rowLabelStyle}>{t.name || `Package ${ti + 1}`}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button type="button" title="Move left" onClick={() => moveTier(ti, -1)} style={iconBtn}><ChevronUp size={13} /></button>
                <button type="button" title="Move right" onClick={() => moveTier(ti, 1)} style={iconBtn}><ChevronDown size={13} /></button>
                <button
                  type="button"
                  onClick={() => updateTier(ti, { featured: !t.featured })}
                  title="Tint this package's column in its badge colour"
                  style={{ ...smallBtn, color: t.featured ? 'var(--color-text)' : 'var(--color-text-muted)' }}
                >
                  {t.featured ? '★ Featured' : '☆ Feature'}
                </button>
                <button
                  type="button"
                  onClick={() => updateTier(ti, { enabled: !t.enabled })}
                  style={{ ...smallBtn, minWidth: 74, justifyContent: 'center' }}
                >
                  {t.enabled ? 'Shown' : 'Hidden'}
                </button>
                <button type="button" title="Remove package" onClick={() => removeTier(ti)} style={iconBtn}><Trash2 size={13} /></button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 1.2fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>Name</label>
                <input type="text" value={t.name} onChange={e => updateTier(ti, { name: e.target.value })} style={{ ...inputStyle, fontSize: 12 }} />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>Price</label>
                <input type="text" value={t.price} onChange={e => updateTier(ti, { price: e.target.value })} placeholder="$2,500" style={{ ...inputStyle, fontSize: 12 }} />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>Price note</label>
                <input type="text" value={t.price_note} onChange={e => updateTier(ti, { price_note: e.target.value })} placeholder="per year" style={{ ...inputStyle, fontSize: 12 }} />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>ID</label>
                <input type="text" value={t.id} onChange={e => updateTier(ti, { id: e.target.value })} style={{ ...inputStyle, fontSize: 12 }} spellCheck={false} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>Badge</label>
                <input
                  type="text"
                  value={t.badge}
                  onChange={e => updateTier(ti, { badge: e.target.value })}
                  placeholder="Most Popular / Supplies Limited"
                  style={{ ...inputStyle, fontSize: 12 }}
                />
                <p style={hintStyle}>Ribbon above the package name. Leave blank for none.</p>
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: 11 }}>Badge colour</label>
                <select value={t.badge_tone} onChange={e => updateTier(ti, { badge_tone: e.target.value })} style={{ ...inputStyle, fontSize: 12 }}>
                  {TONE_CHOICES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <p style={hintStyle}>Also tints the column when “Featured” is on.</p>
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ ...labelStyle, fontSize: 11 }}>Blurb</label>
              <textarea value={t.blurb} onChange={e => updateTier(ti, { blurb: e.target.value })} rows={2} style={{ ...textareaStyle, fontSize: 12 }} />
              <p style={hintStyle}>Shown on the mobile package cards only — the desktop grid has no room for it.</p>
            </div>

            <label style={{ ...labelStyle, fontSize: 11 }}>Included in this package</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 6 }}>
              {pricing.features.map((f, fi) => (
                <button
                  key={fi}
                  type="button"
                  onClick={() => toggleInclude(ti, fi)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', cursor: 'pointer',
                    border: '1px solid ' + (t.includes[fi] ? 'transparent' : 'var(--color-border)'),
                    background: t.includes[fi] ? 'var(--color-primary, #dc2626)' : 'transparent',
                    color: t.includes[fi] ? '#fff' : 'var(--color-text-muted)',
                    borderRadius: 6, padding: '7px 10px', fontSize: 12, lineHeight: 1.35,
                  }}
                >
                  <span style={{ fontWeight: 700, flexShrink: 0 }}>{t.includes[fi] ? '✓' : '—'}</span>
                  <span>{f}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
        <button type="button" onClick={addTier} style={smallBtn}>
          <Plus size={12} /> Add package
        </button>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={save}
          disabled={saving}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--color-primary, #dc2626)',
            color: '#fff', border: 'none', borderRadius: 6, padding: '9px 16px', fontSize: 13, fontWeight: 600,
            cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          <Save size={15} /> {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={restoreDefaults} disabled={saving} style={smallBtn}>
          <RotateCcw size={13} /> Restore defaults
        </button>
        <a
          href="https://watch.tenband.tv/advertise#sponsorship-offers"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-muted)' }}
        >
          <ExternalLink size={13} /> Preview the pricing chart
        </a>
      </div>
      {status && <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '10px 0 0' }}>{status}</p>}
    </div>
  )
}
